import { Injectable, Injector } from '@angular/core';
import { AnswerInterface, Entity, Question, QuestionResponse, Section } from '../interfaces/questions.interface';
import { AuthService } from '../../auth/services/auth.service';
import { CstQuestionnaireService } from './cst-questionnaire.service';
import { UserPermission } from '../../auth/enums/user-permission.enum';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ConditionalLogicTargetQuestions, SingleChoiceOption } from '../interfaces/single-selection-question.interface';
import { MultipleSelectionOptions } from '../interfaces/multiple-selection.question';
import * as _ from 'lodash';
import { CustomerQuestionnaireService } from './customer-questionnaire.service';
import { CifcReview } from '../interfaces/cifc.interface';
import { HttpClient } from '@angular/common/http';
import { IdentityService } from '../../auth/services/identity.service';

export interface ConditionalQuestionsHashMap {
    [id: string]: string[];
}

export interface ConditionalQuestionsWithOptionId extends ConditionalLogicTargetQuestions {
    option_id: string;
}

@Injectable({
    providedIn: 'root',
})
export class CommonQuestionnaireService {
    public createMode = false;
    public reviewMode = false;
    public questionnaireId = '';

    public cstQuestionnaireServiceLoaded = new BehaviorSubject<boolean>(false);
    public customerQuestionnaireServiceLoaded = new BehaviorSubject<boolean>(false);
    public questionnaireIdLoadedSubject = new BehaviorSubject<boolean>(false);
    public questionsFilteredSubject = new BehaviorSubject<boolean>(false);
    public reviewAddedSubject = new Subject<CifcReview>();
    public questionChangedOnReviewResetSubject = new Subject<Question>();
    public cancelReviewSubject = new BehaviorSubject<Question>({} as Question);

    public conditionalQuestionsMap: ConditionalQuestionsHashMap = {};
    public currentEntity: Entity;
    public reviewQuestions: Question[] = [];
    public reviewQuestionsSubject = new BehaviorSubject<Question[]>([] as Question[]);

    public initialReviewQuestions: Question[] = [];

    public filteredSectionsSubject: Subject<Section[]> = new Subject<Section[]>();
    public filteredSections: Section[] = [];

    /** global hashmap for local reviews */
    public cifcReviewMapping: { [key: string]: CifcReview } = {};
    public reviewCompanyName = 'Loading company name';
    public reviewCompanyId: string;
    public displayOkCifc: boolean[] = [];
    public isPublishInProgress: boolean = false;
    private cstQuestionnaireService: CstQuestionnaireService;
    private customerQuestionnaireService: CustomerQuestionnaireService;
    private changeSelectionOnChoiceQuestionSubject: Subject<any> = new Subject<any>();
    private companyId: string;

    constructor(
        private authService: AuthService,
        private identityService: IdentityService,
        private injector: Injector,
        private httpModule: HttpClient,
    ) {
        if (window.location.href.includes('sign-up')) {
            return;
        }

        /** Declare other services through injection to avoid Service Circular Dependency */
        setTimeout(() => {
            this.cstQuestionnaireService = this.injector.get<CstQuestionnaireService>(CstQuestionnaireService);
            this.cstQuestionnaireServiceLoaded.next(true);
        });
        setTimeout(async () => {
            this.customerQuestionnaireService = this.injector.get<CustomerQuestionnaireService>(CustomerQuestionnaireService);
            this.customerQuestionnaireServiceLoaded.next(true);
            const hasRoles = await this.authService
                .hasAtLeastOneRole$([UserPermission.QuestionnaireQuestionsRead, UserPermission.QuestionnaireQuestionWrite])
                .toPromise();
            this.createMode = (await this.authService.hasAtLeastOneRole$([UserPermission.QuestionnaireQuestionWrite]).toPromise()) || false;
            this.reviewMode = (await this.authService.hasAtLeastOneRole$([UserPermission.QuestionnaireAnswerReview]).toPromise()) || false;
            if (hasRoles) {
                this.identityService.companyLoaded$.subscribe((isloaded) => {
                    if (isloaded) {
                        this.customerQuestionnaireService.getQuestionnaireId();
                    }
                });
            }
        });
    }

    public startReviewIfNotStarted(cid: string) {
        return this.httpModule.post(`/v2/customer-questionnaire/questionnaire/start-review/${this.questionnaireId}/${cid}`, {});
    }

    public onChoiceChange(): Observable<boolean> {
        return this.changeSelectionOnChoiceQuestionSubject.asObservable();
    }

    public questionnnaireIdLoadedObs(): Observable<boolean> {
        return this.questionnaireIdLoadedSubject.asObservable();
    }

    public reviewAddedObs(): Observable<CifcReview> {
        return this.reviewAddedSubject.asObservable();
    }

    public cancelReviewObs(): Observable<Question> {
        return this.cancelReviewSubject.asObservable();
    }

    public questionChangedOnReviewResetObs(): Observable<Question> {
        return this.questionChangedOnReviewResetSubject.asObservable();
    }

    public questionsFilteredObs(): Observable<boolean> {
        return this.questionsFilteredSubject.asObservable();
    }

    public removeEntityFromReviewMapping(id: string) {
        delete this.cifcReviewMapping[id];
    }

    public addEntityToReviewMapping(id: string = '', review: CifcReview) {
        this.cifcReviewMapping[id] = review;
    }

    public getReviewFromMapByQuestionId(id: string = ''): CifcReview | null {
        return this.cifcReviewMapping[id];
    }

    /**
     * Function used for changing the background of a question when selected
     */
    public onQuestionSelected(question: QuestionResponse, section?: Section, automaticallySelected = true) {
        if (this.createMode && section) {
            this.cstQuestionnaireService.setQuestion(question as unknown as Question);
            this.cstQuestionnaireService.currentSectionSubject.next(section);
        }

        /** Deselect first all active question templates */
        const questionTemplates = document.getElementsByClassName(
            this.createMode ? 'create-question-template' : 'customer-question-template',
        );

        for (let i = 0; i < questionTemplates.length; i++) {
            questionTemplates[i].classList.remove('active');
        }

        /** Select only the one that was clicked */
        this.markEntityAsActive({ entity: question as unknown as Question });

        if (!this.createMode && !this.reviewMode && !automaticallySelected) {
            this.customerQuestionnaireService.showCheckSubmit = false;
            this.customerQuestionnaireService.questionnaireResumed = true;
        }

        /** In case the user finished the questionnaire but clicks on a question he
         should be left to update the question */
        if (this.customerQuestionnaireService.hasSubmittedQuestionnaire) {
            this.cstQuestionnaireServiceLoaded.next(true);
            this.customerQuestionnaireService.questionnaireResumed = true;
            this.customerQuestionnaireService.hasSubmittedQuestionnaire = false;
        }
    }

    /***
     * When the question exists in DOM, we can highlight it
     * @param entity
     */
    public markEntityAsActive(entity: Entity) {
        const element = this.cstQuestionnaireService.instanceOfSection(entity.entity) ? 'section' : 'question';
        const selector = element === 'section' ? '.' : '#';

        this.waitForElement(`${selector}${element}-` + entity.entity?.id).then((el: any) => {
            /** Deselect first all active question templates */
            const questionTemplates = document.getElementsByClassName(
                this.createMode ? 'create-question-template' : 'customer-question-template',
            );
            const sectionTemplates = document.getElementsByClassName('section-panel-header');

            for (let i = 0; i < questionTemplates.length; i++) {
                questionTemplates[i].classList.remove('active');
            }
            for (let i = 0; i < sectionTemplates.length; i++) {
                sectionTemplates[i].classList.remove('active');
            }

            element === 'question' && (document.getElementById(`${element}-` + entity.entity?.id) as HTMLElement).classList.add('active');
            element === 'section' &&
                (document.getElementsByClassName(`${element}-` + entity.entity?.id)[0] as HTMLElement).classList.add('active');
            this.onQuestionClicked(entity);
        });
    }

    public onQuestionClicked(entity: Entity) {
        if (this.createMode) {
            this.cstQuestionnaireService.setEntity(entity);
        } else {
            if (this.customerQuestionnaireService) {
                this.cstQuestionnaireService.instanceOfQuestion(entity.entity) &&
                    this.customerQuestionnaireService.setQuestion(entity.entity);
            }
        }
    }

    /**
     * Use MutationObserver to wait for a specific element to exist inside DOM.
     * @param selector
     */
    public waitForElement(selector: string) {
        return new Promise((resolve) => {
            if (document.querySelector(selector)) {
                return resolve(document.querySelector(selector));
            }

            const observer = new MutationObserver((mutations) => {
                if (document.querySelector(selector)) {
                    resolve(document.querySelector(selector));
                    observer.disconnect();
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        });
    }

    public resetCifcQuestionToOriginalState(question: Question) {
        let currentQuestionIndex = this.reviewQuestions.findIndex((item) => item.id === question.id);
        let initialQuestion = _.cloneDeep(this.initialReviewQuestions.find((item) => item.id === question.id));

        if (currentQuestionIndex > -1 && initialQuestion) {
            this.reviewQuestions[currentQuestionIndex].body.options = [].concat(initialQuestion?.body.options as never);
            this.reviewQuestionsSubject.next([...this.reviewQuestions]);
            this.questionChangedOnReviewResetSubject.next(this.reviewQuestions[currentQuestionIndex]);
        }
    }

    public addToConditionalQuestionsMap(currentAnswer: SingleChoiceOption[], option: SingleChoiceOption) {
        if (option.target_question_list?.length) {
            option.target_question_list.forEach((item: ConditionalLogicTargetQuestions) => {
                if (!this.conditionalQuestionsMap[item.question_id]?.length) {
                    this.conditionalQuestionsMap[item.question_id] = [];
                }

                if (option.checked && option.target_question_list) {
                    for (const targetQuestion of option.target_question_list) {
                        for (const section of this.customerQuestionnaireService.sections) {
                            const question = section.questions.find((q: Question) => q.id === targetQuestion.question_id);

                            if (!question) {
                                continue;
                            }

                            (question as unknown as QuestionResponse).body.deleted = false;
                        }
                    }
                }

                if (!this.conditionalQuestionsMap[item.question_id].find((x: string) => x === option.id)) {
                    this.conditionalQuestionsMap[item.question_id].push(option.id);
                }
            });
        }
        this.removeUncheckedOptions(currentAnswer);
        if (this.reviewMode) {
            this.reviewQuestions =
                this.customerQuestionnaireService.displayVersion === 'full'
                    ? this.customerQuestionnaireService.getAnsweredQuestionsForReview()
                    : this.customerQuestionnaireService.getMandatoryQuestionsForReview();
            let deleteQ: (string | undefined)[] = [];
            this.reviewQuestions.forEach((reviewQ) => {
                if (this.conditionalQuestionsMap[reviewQ.id || '']?.length === 0) {
                    deleteQ.push(reviewQ.id);
                }
            });
            if (deleteQ.length > 0) {
                this.reviewQuestions.splice(
                    this.reviewQuestions.findIndex((x) => deleteQ.includes(x.id)),
                    1,
                );
            }

            this.reviewQuestionsSubject.next([...this.reviewQuestions]);
            this.customerQuestionnaireService.sections.forEach((s) => {
                s.questions.forEach((q) => {
                    if (deleteQ.includes(q.id)) {
                        (q as unknown as QuestionResponse).body.deleted = true;
                    }
                });
            });
            // this.customerQuestionnaireService.questionnaireLoadedSubject.next(true);
        }
    }

    public removeUncheckedOptions(currentAnswer: SingleChoiceOption[]) {
        currentAnswer.forEach((opt: SingleChoiceOption) => {
            if (!opt.checked) {
                opt.target_question_list?.forEach((item: ConditionalLogicTargetQuestions) => {
                    if (this.conditionalQuestionsMap[item.question_id] && this.conditionalQuestionsMap[item.question_id].includes(opt.id)) {
                        this.removeTargetById(item.question_id, opt.id);

                        const childTargets = this.getChildConditionalTargetsByQuestionId(item.question_id, []);
                        childTargets.forEach((target: ConditionalQuestionsWithOptionId) => {
                            this.removeTargetById(target.question_id, target.option_id);
                        });
                    }
                });
            }
        });
    }

    public removeTargetById(questionId: string, optId: string) {
        const index = this.conditionalQuestionsMap[questionId]?.findIndex((x: string) => x === optId);
        this.conditionalQuestionsMap[questionId]?.splice(index, 1);

        const removedQuestionBody = this.customerQuestionnaireService.processedFullSections.find(
            (question: Question) => question.id === questionId,
        )?.body as unknown as AnswerInterface;

        if (!removedQuestionBody?.answered) {
            return;
        }
    }

    public getChildConditionalTargetsByQuestionId(
        questionId: string,
        result: ConditionalQuestionsWithOptionId[],
    ): ConditionalQuestionsWithOptionId[] {
        let question = this.customerQuestionnaireService.processedFullSections.find((question: Question) => question.id === questionId);

        if (question && (this.instanceOfSingleChoice(question.body.options) || this.instanceOfMultipleChoice(question.body.options))) {
            question.body.options.forEach((opt: SingleChoiceOption) => {
                if (opt.checked && opt.target_question_list?.length) {
                    opt.checked = false;

                    const convertedOptions = opt.target_question_list as unknown as ConditionalQuestionsWithOptionId[];

                    convertedOptions.map((option: ConditionalQuestionsWithOptionId) => (option.option_id = opt.id));
                    result.push(...convertedOptions);

                    opt.target_question_list?.forEach((item: ConditionalLogicTargetQuestions) => {
                        result = this.getChildConditionalTargetsByQuestionId(item.question_id, result);
                    });
                }
            });
        }

        return result;
    }

    public hideQuestionsBasedOnHashMap(questions: Question[]): Question[] {
        questions.forEach((question: Question) => {
            if (question.id && this.conditionalQuestionsMap[question.id] && question.conditional_logic) {
                question.hidden = !this.conditionalQuestionsMap[question.id].length;
            } else if (question.id && (!this.conditionalQuestionsMap[question.id] || question.conditional_logic)) {
                question.hidden = true;
            }

            if (!question.conditional_logic) {
                question.hidden = false;
            }
        });

        this.changeSelectionOnChoiceQuestionSubject.next(true);
        this.customerQuestionnaireService.flattenSections();

        return questions.filter((x: Question) => !x.hidden);
    }

    public filterQuestionsByConditionalLogicOnInit() {
        for (const question of this.customerQuestionnaireService.processedFullSections) {
            if (this.instanceOfSingleChoice(question.body.options) || this.instanceOfMultipleChoice(question.body.options)) {
                for (const option of question.body.options) {
                    this.addToConditionalQuestionsMap(question.body.options as SingleChoiceOption[], option);
                }
            }
        }
    }

    public onSelectionChangeSingle(question: Question, option: SingleChoiceOption, currentAnswer: SingleChoiceOption[]) {
        currentAnswer.forEach((opt: SingleChoiceOption) => {
            opt.checked = false;

            if (opt.id === option.id) {
                opt.checked = true;
                opt = option;
            }
        });

        this.addToConditionalQuestionsMap(currentAnswer, option);
    }

    public onSelectionChangeMultiple(option: SingleChoiceOption, currentAnswer: SingleChoiceOption[]) {
        const currentOptions = _.cloneDeep(currentAnswer);
        currentOptions.forEach((opt: SingleChoiceOption) => {
            opt.checked = false;

            if (opt.id === option.id) {
                opt.checked = true;
                opt = option;
            }
        });

        this.addToConditionalQuestionsMap(currentAnswer, option);
    }

    public setCompanyId(cid: string) {
        this.companyId = cid;
    }

    public instanceOfSingleChoice(object: any): object is SingleChoiceOption[] {
        let isTargetListPresent = false;

        for (let i = 0; i < object.length; i++) {
            if (object[i].target_question_list && object[i].target_question_list.length > 0) {
                isTargetListPresent = true;
            }
        }
        return isTargetListPresent;
    }

    public instanceOfMultipleChoice(object: any): object is MultipleSelectionOptions[] {
        let isTargetListPresent = false;

        for (let i = 0; i < object.length; i++) {
            if (object[i].target_question_list && object[i].target_question_list.length > 0) {
                isTargetListPresent = true;
            }
        }
        return isTargetListPresent;
    }

    public getConditionalQuestionNumber(section: Section, question: Question): number {
        let sum = 0;

        for (const item of this.cstQuestionnaireService.sections) {
            if (item.id === section.id) {
                break;
            }

            sum += item.questions.length;
        }

        return sum + question.number + 1;
    }

    public showReviewWarningMark(question: QuestionResponse): boolean {
        if (!question.cifc_review || this.createMode) {
            return false;
        }

        if (!this.createMode && !this.reviewMode) {
            return question.cifc_review && !question.cifc_review.solved && !(<boolean>question.cifc_review.answered);
        }

        return question.cifc_review && !!question.cifc_review.id && !question.cifc_review.solved;
    }

    public showCheckMark(question: QuestionResponse): boolean {
        if (this.reviewMode) {
            return <boolean>question.body.accepted && !this.showReviewWarningMark(question) && !question.body.updated;
        }

        if (!this.reviewMode && !this.createMode) {
            return question.body.answered && !this.showReviewWarningMark(question);
        }

        return false;
    }

    public showPendingMark(question: QuestionResponse): boolean {
        return this.showReviewWarningMark(question) && !this.cstQuestionnaireService.isQuestionAnsweredAfterReview(question);
    }

    public isQuestionBodyModified(question: Question, originalQuestion: Question) {
        return JSON.stringify(question.body.options) !== JSON.stringify(originalQuestion.body.options);
    }
}
